import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
} from '@angular/core';
import { arc, pie } from 'd3-shape';
import { ObservableInput } from 'observable-input';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith, withLatestFrom } from 'rxjs/operators';
import { Size } from '../core/size.service';
import { LabeledDataPoint, PreparedDataPoint } from './data';
import { combinePrepared, SortType } from './helpers/combine-tail';

const MAX_ARCS = 15;

@Component({
  selector: 'prepared-donut',
  templateUrl: './donut.component.html',
  styleUrls: ['./donut.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreparedDonutComponent<T> {
  @ObservableInput()
  @Input()
  data!: Observable<PreparedDataPoint<T>[] | null>;

  @ObservableInput()
  @Input()
  getMetric!: Observable<(metricData: T) => number>;

  @ObservableInput()
  @Input()
  tailReducer!: Observable<(items: T[]) => T>;

  @ObservableInput()
  @Input()
  valueFormatter!: Observable<(value: number) => string>;

  @ObservableInput()
  @Input('sorted')
  private _sorted!: Observable<SortType>;
  sorted: Observable<SortType> = this._sorted.pipe(startWith(<SortType>'DESC'));

  @ObservableInput()
  @Input()
  clickable!: Observable<boolean>;

  readonly dataPoints: Observable<LabeledDataPoint[]> = combineLatest([
    this.data,
    this.getMetric,
    this.tailReducer,
    this.sorted,
  ]).pipe(
    filter(([data]) => data !== null),
    map(([data, getMetric, reducer, sorted]) =>
      combinePrepared(data!, MAX_ARCS, getMetric, reducer, sorted),
    ),
  );

  readonly onClick = new Subject<number>();

  @Output()
  readonly click = this.onClick.pipe(
    withLatestFrom(this.dataPoints),
    map(([index, dataPoints]) => {
      return dataPoints[index];
    }),
  );

  readonly empty = this.dataPoints.pipe(map(points => points.length === 0));

  readonly total = this.dataPoints.pipe(
    map(points => points.reduce((total, point) => total + point.value, 0)),
  );

  readonly svgSize = new Subject<Size>();

  readonly radii = this.svgSize.pipe(
    map(size => {
      const outer = (Math.min(size.width, size.height) * 0.9) / 2;
      const inner = outer - 12;
      return { inner, outer };
    }),
  );

  private readonly pieArcs = this.dataPoints.pipe(
    map(points =>
      pie<LabeledDataPoint>()
        .sortValues(null)
        .value(d => d.value)(points),
    ),
  );

  readonly arcPaths = combineLatest([this.pieArcs, this.radii]).pipe(
    map(([arcs, { inner, outer }]) =>
      arcs.map(pieArc =>
        arc<LabeledDataPoint>()
          .innerRadius(inner)
          .outerRadius(outer)
          .padAngle(0.013)
          .startAngle(pieArc.startAngle)
          .endAngle(pieArc.endAngle)(pieArc.data),
      ),
    ),
  );

  readonly hitAreaRadii = this.svgSize.pipe(
    map(size => ({
      inner: Math.min(size.width, size.height) / 4,
      outer: Math.min(size.width, size.height) / 2,
    })),
  );

  readonly hitAreaArcPaths = combineLatest([
    this.pieArcs,
    this.hitAreaRadii,
  ]).pipe(
    map(([arcs, { inner, outer }]) =>
      arcs.map(pieArc =>
        arc<LabeledDataPoint>()
          .innerRadius(inner)
          .outerRadius(outer)
          .startAngle(pieArc.startAngle)
          .endAngle(pieArc.endAngle)(pieArc.data),
      ),
    ),
  );

  readonly hoveredIndex = new Subject<number | null>();
}
